import React, { useState, useEffect, useMemo } from 'react';
import driverCompatibilityService from '../services/driverCompatibilityService';

function DriverSelector({ 
  value, 
  onChange, 
  loadRequirements,
  scheduledDate,
  disabled = false 
}) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [pendingOverride, setPendingOverride] = useState(null);

  // __DRIVER_SELECTOR_ORDER__ (2026-09-13) TWO faults lived in these few lines.
  //
  // 1. THE COMPONENT NEVER MOUNTED. The useEffect below used to sit ABOVE the
  //    callback and list it as a dependency. A dependency array is built while the
  //    component renders, at the moment useEffect is CALLED -- which is before the
  //    `const` underneath it initialises. Every render therefore threw
  //    "ReferenceError: Cannot access 'fetchDriversWithCompatibility' before
  //    initialization". The driver list was not empty, it was absent: the selector
  //    threw before it could ask the service for anything, so the column-name fix
  //    that went in earlier could not have brought it back on its own.
  //
  // 2. A FETCH PER KEYSTROKE. EditLoadModal builds loadRequirements as an object
  //    literal inside its JSX, so its identity changes on every parent render --
  //    and the parent re-renders on every character typed into the form. Depending
  //    on that identity re-created the callback and re-ran the effect each time,
  //    firing the whole compatibility pass (drivers, their loads, their hours) per
  //    character. Depend on the five VALUES the service actually reads instead;
  //    the object is rebuilt only when one of them changes.
  // __EQUIP_COMPAT__ commodity too: the cargo type decides which equipment it needs.
  // __OPEN_DECK__ the job's Truck required and self-unload too (an open-deck job warns on a tarp / curtain-side unit)
  const {
    weight_kg, length_cm, width_cm, height_cm, commodity, estimated_duration_hours, truck_required, self_unload
  } = loadRequirements || {};
  const requirements = useMemo(
    () => ({ weight_kg, length_cm, width_cm, height_cm, commodity, estimated_duration_hours,
      billing_data: { truck_required: truck_required || undefined, self_unload: self_unload || undefined } }),
    [weight_kg, length_cm, width_cm, height_cm, commodity, estimated_duration_hours, truck_required, self_unload]
  );

  // __DRIVER_SELECTOR_DEBOUNCE__ (2026-09-23) the values above still change on
  // every character typed into weight or a dimension, and each change ran the
  // whole compatibility pass (drivers, their loads, their hours). Wait until
  // typing pauses, and ignore an answer that arrives after a newer question --
  // otherwise a slow early response could overwrite the list for the final value.
  useEffect(() => {
    let stale = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const driversWithCompatibility = await driverCompatibilityService.getDriversWithCompatibility(
          requirements,
          scheduledDate
        );
        if (stale) return;
        setDrivers(driverCompatibilityService.sortDriversByCompatibility(driversWithCompatibility));
      } catch (error) {
        if (!stale) console.error('Error fetching drivers:', error);
      } finally {
        if (!stale) setLoading(false);
      }
    }, 350);
    return () => { stale = true; clearTimeout(t); };
  }, [requirements, scheduledDate]);

  const selectedDriver = drivers.find(d => d.id === value);

  const getStatusColor = (driver) => {
    if (!driver.isSelectable) return 'var(--color-danger)';
    if (driver.hoursStatus === 'over_limit') return 'var(--color-danger)';
    if (driver.hoursStatus === 'near_limit') return 'var(--color-warning)';
    if (driver.isFullyCompatible) return 'var(--accent-primary)';
    return 'var(--color-warning)';
  };

  const getStatusIcon = (driver) => {
    if (!driver.isSelectable) return '⛔';
    if (driver.hoursStatus === 'over_limit') return '⚠️';
    if (driver.hoursStatus === 'near_limit') return '⚠️';
    if (driver.isFullyCompatible) return '✓';
    return '⚠️';
  };

  const handleDriverClick = (driver) => {
    if (disabled) return;
    
    if (!driver.isSelectable) {
      setPendingOverride(driver);
    } else {
      onChange({ target: { name: 'driver_id', value: driver.id } });
      setExpanded(false);
      setPendingOverride(null);
    }
  };

  const confirmOverride = () => {
    if (pendingOverride) {
      onChange({ target: { name: 'driver_id', value: pendingOverride.id } });
      setExpanded(false);
      setPendingOverride(null);
    }
  };

  const cancelOverride = () => {
    setPendingOverride(null);
  };

  const renderDriverOption = (driver) => {
    const driverName = driver.user?.name || driver.name || driver.email || `Driver ${driver.id.slice(0, 8)}`;
    const combination = driver.default_combination;
    const statusColor = getStatusColor(driver);
    const statusIcon = getStatusIcon(driver);
    const isSelected = driver.id === value;
    const isPendingOverride = pendingOverride?.id === driver.id;

    return (
      <div
        key={driver.id}
        onClick={() => handleDriverClick(driver)}
        style={{
          padding: '0.75rem',
          backgroundColor: isSelected ? 'var(--surface-3)' : isPendingOverride ? 'var(--bg-tertiary)' : 'var(--surface-2)',
          borderLeft: `4px solid ${statusColor}`,
          marginBottom: '0.5rem',
          borderRadius: '4px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: !driver.isSelectable ? 0.7 : 1,
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.backgroundColor = isPendingOverride ? 'var(--bg-tertiary)' : 'var(--surface-3)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected && !isPendingOverride) {
            e.currentTarget.style.backgroundColor = 'var(--surface-2)';
          }
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span style={{ color: statusColor, fontSize: '1rem' }}>{statusIcon}</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>{driverName}</span>
              {isSelected && <span style={{ color: 'var(--accent-primary)', fontSize: '0.8rem' }}>(Selected)</span>}
            </div>
            
            {combination ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '1.5rem' }}>
                <div>{combination.combination_name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {combination.truck?.truck_type} + {combination.trailer?.trailer_type || 'No trailer'}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '0.85rem', color: 'var(--color-danger)', marginLeft: '1.5rem' }}>
                No truck or truck-trailer combination assigned
              </div>
            )}
          </div>
          
          <div style={{ textAlign: 'right', fontSize: '0.8rem' }}>
            <div style={{ 
              color: driver.hoursStatus === 'ok' ? 'var(--accent-primary)' : 
                     driver.hoursStatus === 'near_limit' ? 'var(--color-warning)' : 'var(--color-danger)'
            }}>
              {driver.projectedHours.toFixed(1)}h / {driver.maxHours}h
            </div>
            {driver.hoursWarning && (
              <div style={{ color: 'var(--color-warning)', fontSize: '0.75rem', maxWidth: '150px' }}>
                {driver.hoursWarning}
              </div>
            )}
          </div>
        </div>

        {!driver.compatibility.isCompatible && (
          <div style={{ 
            marginTop: '0.5rem', 
            marginLeft: '1.5rem',
            padding: '0.5rem',
            backgroundColor: 'rgba(231, 76, 60, 0.2)',
            borderRadius: '4px',
            fontSize: '0.8rem'
          }}>
            <div style={{ color: 'var(--color-danger)', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              {isPendingOverride ? 'Override Warning:' : 'Incompatible:'}
            </div>
            {driver.compatibility.reasons.map((reason, idx) => (
              <div key={idx} style={{ color: 'var(--color-danger-emphasis)' }}>• {reason}</div>
            ))}
            
            {isPendingOverride && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ color: 'var(--color-warning)', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  Are you sure you want to assign this driver anyway?
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmOverride();
                    }}
                    style={{
                      padding: '0.4rem 0.75rem',
                      backgroundColor: 'var(--color-warning)',
                      color: 'var(--bg-primary)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '0.8rem'
                    }}
                  >
                    Yes, Override
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelOverride();
                    }}
                    style={{
                      padding: '0.4rem 0.75rem',
                      backgroundColor: 'var(--btn-secondary-bg)',
                      color: 'var(--text-primary)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {driver.compatibility.isCompatible && driver.compatibility.warnings.length > 0 && (
          <div style={{ 
            marginTop: '0.5rem', 
            marginLeft: '1.5rem',
            padding: '0.5rem',
            backgroundColor: 'rgba(243, 156, 18, 0.2)',
            borderRadius: '4px',
            fontSize: '0.8rem'
          }}>
            {driver.compatibility.warnings.map((warning, idx) => (
              <div key={idx} style={{ color: 'var(--color-warning)' }}>⚠️ {warning}</div>
            ))}
          </div>
        )}

        {driver.compatibility.isCompatible && expanded && (
          <div style={{ 
            marginTop: '0.5rem', 
            marginLeft: '1.5rem',
            fontSize: '0.8rem',
            color: 'var(--text-muted)'
          }}>
            <div>Weight: {driver.compatibility.details.weight.message}</div>
            <div>Dimensions: {driver.compatibility.details.dimensions.message}</div>
          </div>
        )}
      </div>
    );
  };

  const compatibleCount = drivers.filter(d => d.isFullyCompatible).length;
  const selectableCount = drivers.filter(d => d.isSelectable).length;

  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
        Assign Driver
      </label>
      
      <div style={{ 
        backgroundColor: 'var(--surface-2)', 
        border: '1px solid var(--border-default)', 
        borderRadius: '5px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => !disabled && setExpanded(!expanded)}
          style={{
            padding: '0.75rem',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: expanded ? 'var(--surface-3)' : 'var(--surface-2)'
          }}
        >
          <div>
            {loading ? (
              <span style={{ color: 'var(--text-muted)' }}>Loading drivers...</span>
            ) : selectedDriver ? (
              <span style={{ color: 'var(--text-primary)' }}>
                {selectedDriver.user?.name || selectedDriver.name || selectedDriver.email}
                {selectedDriver.default_combination && (
                  <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                    ({selectedDriver.default_combination.combination_name})
                  </span>
                )}
              </span>
            ) : (
              <span style={{ color: 'var(--text-muted)' }}>Select a driver...</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {compatibleCount}/{drivers.length} compatible
            </span>
            <span style={{ color: 'var(--text-muted)' }}>{expanded ? '▲' : '▼'}</span>
          </div>
        </div>

        {expanded && !loading && (
          <div style={{ 
            maxHeight: '400px', 
            overflowY: 'auto',
            padding: '0.5rem',
            borderTop: '1px solid var(--border-default)'
          }}>
            <div 
              onClick={() => {
                onChange({ target: { name: 'driver_id', value: '' } });
                setExpanded(false);
              }}
              style={{
                padding: '0.75rem',
                backgroundColor: !value ? 'var(--surface-3)' : 'var(--surface-2)',
                marginBottom: '0.5rem',
                borderRadius: '4px',
                cursor: 'pointer',
                color: 'var(--text-muted)'
              }}
            >
              No driver assigned yet
            </div>

            {drivers.length === 0 ? (
              <div style={{ padding: '1rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                No drivers available
              </div>
            ) : (
              <>
                {selectableCount > 0 && (
                  <div style={{ 
                    padding: '0.5rem', 
                    color: 'var(--accent-primary)', 
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    borderBottom: '1px solid var(--border-default)',
                    marginBottom: '0.5rem'
                  }}>
                    Compatible Drivers ({selectableCount})
                  </div>
                )}
                {drivers.filter(d => d.isSelectable).map(renderDriverOption)}
                
                {drivers.filter(d => !d.isSelectable).length > 0 && (
                  <>
                    <div style={{ 
                      padding: '0.5rem', 
                      color: 'var(--color-danger)', 
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      borderBottom: '1px solid var(--border-default)',
                      marginBottom: '0.5rem',
                      marginTop: '1rem'
                    }}>
                      Incompatible Drivers ({drivers.filter(d => !d.isSelectable).length})
                      <span style={{ fontWeight: 'normal', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                        - click to override
                      </span>
                    </div>
                    {drivers.filter(d => !d.isSelectable).map(renderDriverOption)}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {selectedDriver && !selectedDriver.isFullyCompatible && (
        <div style={{ 
          marginTop: '0.5rem', 
          padding: '0.5rem',
          backgroundColor: selectedDriver.isSelectable ? 'rgba(210, 153, 34, 0.2)' : 'rgba(218, 54, 51, 0.2)',
          borderRadius: '4px',
          fontSize: '0.85rem'
        }}>
          {!selectedDriver.compatibility.isCompatible && (
            <div style={{ color: 'var(--color-danger)' }}>
              <strong>Warning:</strong> This driver's equipment may not be suitable for this load.
              {selectedDriver.compatibility.reasons.map((reason, idx) => (
                <div key={idx} style={{ marginLeft: '1rem' }}>• {reason}</div>
              ))}
            </div>
          )}
          {selectedDriver.hoursWarning && (
            <div style={{ color: 'var(--color-warning)', marginTop: selectedDriver.compatibility.reasons.length > 0 ? '0.5rem' : 0 }}>
              <strong>Hours Warning:</strong> {selectedDriver.hoursWarning}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DriverSelector;
